// src/hooks/useHomePendencias.ts
// Contagens de pendências da Home. Molde: `useConfiguracaoLogin` (leitura
// simples com fail-safe) — NÃO é orquestrador de Kanban (ADR-006), pois não
// pagina nem acumula itens.
//
// FRONTEIRA DE MIGRAÇÃO: este arquivo é o ÚNICO ponto que conhece as URLs das
// contagens. Cada número aqui é o `page.totalElements` da mesma rota que
// alimenta a coluna correspondente do Kanban, então a Home é numericamente
// idêntica ao círculo de contagem daquela coluna por construção:
//
//   aprovacao    → coluna "Aguardando aprovação"   de /requisicoes
//   recebimento  → coluna "Aguardando Recebimento" de /conferencia
//   compras      → coluna "Requisições Pendentes"  de /compras
//
// `size=1` porque só o total interessa — o `content` é descartado. Se algum dia
// o backend expuser um `GET /v1/home/resumo`, trocar as 3 chamadas por uma aqui
// é suficiente: a UI consome apenas o shape público abaixo e não muda.
//
// GATE: cada contagem só é buscada quando o usuário tem a permissão granular E
// o tenant tem o módulo — a MESMA regra dos cards da Home. Sem acesso, a chave
// vem `null` (a linha não existe) e NENHUM request é disparado.
import { useEffect, useState } from 'react';
import { apiGet } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useEntitlement } from '@/contexts/EntitlementContext';
import { logger } from '@/utils/logger';

export type PendenciaKey = 'aprovacao' | 'recebimento' | 'compras';

export interface ContagemPendencia {
  /** `loading` enquanto em voo; `error` quando a chamada falhou ou o total veio inválido. */
  status: 'loading' | 'ok' | 'error';
  /** Total de elementos. `null` enquanto carrega e em erro — NUNCA 0 por falha. */
  total: number | null;
}

export interface HomePendencias {
  /** `null` = usuário sem acesso a esta pendência (a linha não deve existir). */
  aprovacao: ContagemPendencia | null;
  recebimento: ContagemPendencia | null;
  compras: ContagemPendencia | null;
  /** `true` enquanto o gate não resolveu ou alguma contagem está em voo. */
  isLoading: boolean;
}

// Cada pendência declara a permissão granular, a área do módulo de tenant e a
// URL. `area` é o sufixo da permissão (`modulo.<area>`), igual à Home.
const FONTES: Record<PendenciaKey, { permissao: string; area: string; url: string }> = {
  aprovacao: {
    permissao: 'modulo.requisicoes',
    area: 'requisicoes',
    url: '/v1/requisicoes/capa?page=0&size=1&itens=aprovar',
  },
  recebimento: {
    permissao: 'modulo.gestao_conferencias',
    area: 'gestao_conferencias',
    url: '/v1/recebimento-conferencia/compra-distribuicao/capa?page=0&size=1&status=ABERTA',
  },
  compras: {
    permissao: 'modulo.gestao_compras',
    area: 'gestao_compras',
    url: '/v1/ordens-compra/capa-compra-itens?page=0&size=1&itens=compra',
  },
};

const CHAVES = Object.keys(FONTES) as PendenciaKey[];

const SEM_ACESSO: HomePendencias = {
  aprovacao: null,
  recebimento: null,
  compras: null,
  isLoading: false,
};

// Total ausente/não-numérico é ERRO, não zero: um zero fabricado viraria
// "tudo em dia" numa fila de aprovação, que é a mentira mais cara desta tela.
const lerTotal = (data: unknown): number | null => {
  const total = (data as { page?: { totalElements?: unknown } } | undefined)?.page?.totalElements;
  return typeof total === 'number' && Number.isFinite(total) ? total : null;
};

export function useHomePendencias(): HomePendencias {
  const { isInitialized, hasPermission } = useAuth();
  const { isAreaEnabled, status: entitlementStatus } = useEntitlement();
  const [contagens, setContagens] = useState<Record<PendenciaKey, ContagemPendencia | null>>({
    aprovacao: null,
    recebimento: null,
    compras: null,
  });
  const [resolvido, setResolvido] = useState(false);

  // O gate de módulo é fail-open enquanto carrega (`isAreaEnabled` devolve
  // `true`), então buscar antes da resposta chegar renderizaria uma linha que
  // depois desapareceria. Esperar `loaded`/`error` custa pouco (a Home é a
  // landing page, o fetch de entitlement já está em voo) e elimina o flicker.
  const gateResolvido = entitlementStatus === 'loaded' || entitlementStatus === 'error';

  // Dependência de efeito como STRING, não como função. `hasPermission` não é
  // memoizado no AuthContext — usar sua identidade como dep dispararia as 3
  // chamadas de novo a cada re-render do AuthProvider. A lista de chaves
  // visíveis é o único dado que o efeito precisa, e é primitiva.
  const visiveisKey = CHAVES.filter(
    (chave) => hasPermission(FONTES[chave].permissao) && isAreaEnabled(FONTES[chave].area)
  ).join(',');

  useEffect(() => {
    if (!isInitialized || !gateResolvido) return;

    const visiveis = (visiveisKey ? visiveisKey.split(',') : []) as PendenciaKey[];
    if (!visiveis.length) {
      setContagens({ aprovacao: null, recebimento: null, compras: null });
      setResolvido(true);
      return;
    }

    let cancelado = false;

    // Estado inicial: só as visíveis entram em `loading`; as demais seguem null.
    setContagens(() => {
      const base: Record<PendenciaKey, ContagemPendencia | null> = {
        aprovacao: null,
        recebimento: null,
        compras: null,
      };
      visiveis.forEach((chave) => {
        base[chave] = { status: 'loading', total: null };
      });
      return base;
    });
    setResolvido(false);

    // Cada contagem se resolve sozinha: uma falhar não afeta as outras.
    // `silent: true` mantém o Busy Overlay fora do caminho — leitura de fundo
    // que o usuário não pediu não pode travar a tela (ver api.ts).
    const buscar = async (chave: PendenciaKey) => {
      try {
        const res = await apiGet(FONTES[chave].url, { silent: true });
        if (cancelado) return;
        const total = lerTotal(res.data);
        setContagens((prev) => ({
          ...prev,
          [chave]: total === null ? { status: 'error', total: null } : { status: 'ok', total },
        }));
      } catch (err) {
        if (cancelado) return;
        // Só a mensagem: o erro Axios cru carrega `config.headers` com o Bearer
        // token (§E). O interceptor de api.ts já registra url/status.
        logger.error(
          `Falha ao contar pendência '${chave}':`,
          err instanceof Error ? err.message : String(err)
        );
        setContagens((prev) => ({ ...prev, [chave]: { status: 'error', total: null } }));
      }
    };

    Promise.all(visiveis.map(buscar)).finally(() => {
      if (!cancelado) setResolvido(true);
    });

    return () => {
      cancelado = true;
    };
  }, [isInitialized, gateResolvido, visiveisKey]);

  if (!isInitialized) return { ...SEM_ACESSO, isLoading: true };

  return { ...contagens, isLoading: !gateResolvido || !resolvido };
}
